/**
 * SiteNav — shared top navigation. Used by the homepage AND the new
 * per-section pages (/pricing, /products, /portfolio, /tos).
 *
 * Links go to REAL routes (not hash anchors) so pasting a direct link works.
 * Premium floating glass pill design (LA Cars-inspired, monochrome).
 */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tags, ShoppingBag, Images, FileText, Menu, X, Ticket } from "lucide-react";
import CfxLoginButton from "@/components/CfxLoginButton";
import CartButton from "@/components/CartButton";

export default function SiteNav({ config }) {
  const settings = config?.settings || {};
  const discord = settings.discord_url || "#";
  const hasPortfolio = (config?.portfolio?.length ?? 0) > 0;
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  const raffle = settings.raffle || {};
  const showRaffle = !!raffle.status && raffle.status !== "hidden";
  const raffleLive = raffle.status === "active";

  const links = [
    { to: "/pricing", label: "Pricing", icon: Tags, testid: "nav-pricing" },
    { to: "/products", label: "Shop", icon: ShoppingBag, testid: "nav-products" },
    ...(hasPortfolio
      ? [{ to: "/portfolio", label: "Portfolio", icon: Images, testid: "nav-portfolio" }]
      : []),
    ...(showRaffle
      ? [{ to: "/raffle", label: "Raffle", icon: Ticket, testid: "nav-raffle", live: raffleLive }]
      : []),
    { to: "/tos", label: "ToS", icon: FileText, testid: "nav-tos" },
  ];

  return (
    <header data-testid="site-nav" className="fixed top-0 left-0 right-0 z-50 h-16 pointer-events-none">
      <div className="max-w-7xl mx-auto px-3 md:px-6 pt-2.5">
        <div className="pointer-events-auto flex items-center justify-between gap-3 h-[52px] px-3 md:px-5 rounded-full border border-[var(--theme-border)] bg-[var(--theme-bg)]/70 backdrop-blur-xl shadow-[0_8px_40px_-12px_rgba(0,0,0,0.8)]">
          <Link to="/" className="flex items-center gap-2.5 group min-w-0" data-testid="nav-home">
            <img
              src="/logo.png?v=4"
              alt="Null Modifications"
              className="h-8 w-8 object-contain group-hover:scale-110 transition-transform"
              data-testid="nav-logo"
            />
            <span className="font-display text-lg md:text-xl tracking-[0.08em] uppercase whitespace-nowrap truncate">
              Null Modifications
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map(({ to, label, icon: Icon, testid, live }) => {
              const active = pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.14em] transition-colors ${
                    active
                      ? "bg-[var(--theme-accent-primary)]/10 text-[var(--theme-text)] border border-[var(--theme-accent-primary)]/30"
                      : "text-[var(--theme-text-muted)] hover:text-[var(--theme-text)] border border-transparent"
                  }`}
                >
                  <Icon size={13} />
                  {label}
                  {live && <span className="nm-live-badge" data-testid={`${testid}-live`}>LIVE</span>}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <CfxLoginButton />
            <CartButton />
            <a
              data-testid="nav-discord"
              href={discord}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Join our Discord"
              title="Join us on Discord"
              className="group relative w-10 h-10 hidden sm:flex items-center justify-center rounded-full border border-[var(--theme-border)] text-[var(--accent)] hover:border-[var(--theme-accent-primary)]/60 hover:bg-[var(--theme-accent-primary)]/10 transition-colors"
            >
              <svg
                viewBox="0 -2 24 28"
                preserveAspectRatio="xMidYMid meet"
                className="w-5 h-5 group-hover:drop-shadow-[0_0_12px_rgba(var(--theme-accent-primary-rgb),0.8)] transition"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.51 12.51 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.011 14.18 14.18 0 0 0 12.062 0 .074.074 0 0 1 .078.01c.12.099.246.198.373.292a.077.077 0 0 1-.007.128 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.029 19.88 19.88 0 0 0 6.002-3.03.077.077 0 0 0 .031-.055c.5-5.177-.838-9.674-3.548-13.66a.061.061 0 0 0-.031-.029zM8.02 15.33c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.095 2.157 2.418 0 1.334-.955 2.42-2.157 2.42zm7.974 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.418 2.157-2.418 1.211 0 2.176 1.095 2.157 2.418 0 1.334-.946 2.42-2.157 2.42z"/>
              </svg>
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-[var(--theme-border)] text-[var(--theme-text)] hover:bg-[var(--theme-accent-primary)]/10 transition-colors"
            >
              {mobileOpen ? <X size={17} /> : <Menu size={17} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div
            data-testid="nav-mobile-menu"
            className="pointer-events-auto md:hidden mt-2 rounded-2xl border border-[var(--theme-border)] bg-[var(--theme-bg)]/95 backdrop-blur-xl shadow-[0_16px_60px_-12px_rgba(0,0,0,0.9)] p-2"
          >
            {links.map(({ to, label, icon: Icon, testid, live }) => (
              <Link
                key={to}
                to={to}
                data-testid={`${testid}-mobile`}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-[0.16em] transition-colors ${
                  pathname === to
                    ? "bg-[var(--theme-accent-primary)]/10 text-[var(--theme-text)]"
                    : "text-[var(--theme-text-muted)] hover:text-[var(--theme-text)] hover:bg-[var(--theme-accent-primary)]/5"
                }`}
              >
                <Icon size={14} />
                {label}
                {live && <span className="nm-live-badge">LIVE</span>}
              </Link>
            ))}
            <a
              href={discord}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-discord-mobile"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-[0.16em] text-[var(--theme-text-muted)] hover:text-[var(--theme-text)] hover:bg-[var(--theme-accent-primary)]/5 transition-colors"
            >
              <svg viewBox="0 -2 24 28" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.51 12.51 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.011 14.18 14.18 0 0 0 12.062 0 .074.074 0 0 1 .078.01c.12.099.246.198.373.292a.077.077 0 0 1-.007.128 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.029 19.88 19.88 0 0 0 6.002-3.03.077.077 0 0 0 .031-.055c.5-5.177-.838-9.674-3.548-13.66a.061.061 0 0 0-.031-.029zM8.02 15.33c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.095 2.157 2.418 0 1.334-.955 2.42-2.157 2.42zm7.974 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.418 2.157-2.418 1.211 0 2.176 1.095 2.157 2.418 0 1.334-.946 2.42-2.157 2.42z"/>
              </svg>
              Discord
            </a>
          </div>
        )}
      </div>
    </header>
  );
}
